import { useState, useEffect, useRef } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';

/* ── Animated Sparkline with Hover Glow ── */
function AnimatedSparkline({ data, color, active, onActivate, idx }: {
  data: number[];
  color: string;
  active: boolean;
  onActivate: () => void;
  idx: number;
}) {
  const maxVal = Math.max(...data);
  const w = 200, h = 32;

  const pathD = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / maxVal) * (h - 2);
    return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
  }).join(' ');

  const fillD = `${pathD} L${w} ${h} L0 ${h} Z`;

  return (
    <div
      className="h-8 w-full overflow-hidden cursor-pointer transition-all duration-300"
      onMouseEnter={onActivate}
    >
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`stat-spark-${idx}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity={active ? '0.25' : '0.1'} />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
          <filter id={`glow-${idx}`}>
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Fill area */}
        <path d={fillD} fill={`url(#stat-spark-${idx})`} className="transition-all duration-500" />
        {/* Line */}
        <path
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth={active ? '1.6' : '1.1'}
          strokeOpacity={active ? '0.9' : '0.6'}
          className={`transition-all duration-500 ${active ? '' : ''}`}
          filter={active ? `url(#glow-${idx})` : undefined}
        />
        {/* End dot */}
        <circle
          cx={w}
          cy={h - (data[data.length - 1] / maxVal) * (h - 2)}
          r={active ? '3' : '1.5'}
          fill={color}
          opacity={active ? '0.9' : '0.5'}
          className="transition-all duration-300"
        >
          {active && <animate attributeName="r" values="3;4.5;3" dur="2s" repeatCount="indefinite" />}
        </circle>
      </svg>
    </div>
  );
}

/* ── Live YoY Badge ── */
function LiveYoY({ label, initial, range }: { label: string; initial: number; range: number }) {
  const { formatted, trend, flash } = useLiveData({ initial, range, decimals: 1, interval: 3000, format: 'percent' });
  return (
    <span className={`text-[10px] mono-data font-semibold transition-all duration-300 ${flash ? 'data-tick' : ''} ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-foreground-600'}`}>
      +{formatted} YoY
    </span>
  );
}

const stats = [
  {
    value: '50+',
    label: '量化策略模型',
    detail: '覆盖多因子 / CTA / 统计套利 / 波动率曲面，为资管业务提供策略引擎',
    icon: 'ri-brain-line',
    trend: [30, 35, 32, 40, 38, 45, 42, 48, 46, 52, 50, 55, 53, 58, 56, 60, 57, 62, 59, 65],
    countTarget: 50,
    countSuffix: '+',
    liveYoY: { initial: 12.4, range: 0.3 },
    color: '#48D1B0',
  },
  {
    value: '200+',
    label: '服务企业与机构',
    detail: '涵盖亚太与欧洲核心金融市场',
    icon: 'ri-building-line',
    trend: [120, 128, 132, 140, 145, 152, 155, 160, 165, 172, 175, 180, 185, 190, 195, 198, 200, 204, 208, 212],
    countTarget: 200,
    countSuffix: '+',
    liveYoY: { initial: 18.2, range: 0.4 },
    color: '#60A8E0',
  },
  {
    value: '15+',
    label: '年行业深耕经验',
    detail: '核心团队来自全球顶级金融机构',
    icon: 'ri-time-line',
    trend: [3, 5, 7, 8, 10, 11, 12, 13, 14, 15, 15, 16, 16, 17, 17, 18, 18, 19, 19, 20],
    countTarget: 15,
    countSuffix: '+',
    liveYoY: { initial: 22.1, range: 0.5 },
    color: '#C8A840',
  },
  {
    value: '$2.8B+',
    label: '管理资产规模 (AUM)',
    detail: '含量化策略、基金组合与家办资产',
    icon: 'ri-funds-box-line',
    trend: [80, 95, 110, 125, 140, 155, 168, 180, 192, 205, 215, 228, 238, 250, 260, 268, 275, 280, 282, 285],
    countTarget: 2.8,
    countSuffix: 'B+',
    countPrefix: '$',
    countDecimals: 1,
    liveYoY: { initial: 22.5, range: 0.6 },
    color: '#8844CC',
  },
];

export default function StatsSection() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      <div className="absolute inset-0 bg-fintech-grid-sm opacity-25"></div>
      <div className="absolute inset-0 bg-dot-matrix opacity-20"></div>

      <div className="absolute left-0 top-[8%] bottom-[8%] w-px bg-gradient-to-b from-transparent via-primary-400/12 to-transparent"></div>
      <div className="absolute right-0 top-[8%] bottom-[8%] w-px bg-gradient-to-b from-transparent via-accent-400/8 to-transparent"></div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full bg-primary-500/[0.02] blur-[140px] pointer-events-none"></div>

      <div className="relative px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto">

          {/* Terminal-style header with live pulse */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400/50"></span>
              <span className="w-2 h-2 rounded-full bg-accent-400/50"></span>
              <span className="w-2 h-2 rounded-full bg-emerald-400/50"></span>
            </div>
            <span className="mono-data text-[10px] text-foreground-600 tracking-[0.15em]">GROUP_OVERVIEW :: LIVE_DATA_PANEL v3.0</span>
            <span className="w-px h-3 bg-background-400/30"></span>
            <span className="text-[10px] text-emerald-400 font-semibold mono-data">LIVE</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
            <span className="text-[10px] mono-data text-foreground-600 animate-blink">◉ SYNC</span>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {stats.map((stat, idx) => (
              <StatCard
                key={idx}
                stat={stat}
                idx={idx}
                visible={visible}
                isActive={activeIdx === idx}
                onActivate={() => setActiveIdx(idx === activeIdx ? null : idx)}
              />
            ))}
          </div>

          {/* Bottom disclaimer */}
          <div className="mt-10 text-center">
            <p className="text-[10px] md:text-xs text-foreground-600/50 max-w-3xl mx-auto leading-relaxed">
              以上数据截至2026年6月，仅供品牌展示参考，不构成任何投资建议或业绩承诺。投资有风险，过往业绩不预示未来表现。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Individual Stat Card ── */
function StatCard({ stat, idx, visible, isActive, onActivate }: {
  stat: typeof stats[0];
  idx: number;
  visible: boolean;
  isActive: boolean;
  onActivate: () => void;
}) {
  const { formatted } = useCountUp({
    target: stat.countTarget,
    duration: 2200,
    delay: idx * 200,
    prefix: stat.countPrefix || '',
    suffix: stat.countSuffix || '',
    decimals: stat.countDecimals || 0,
    enabled: visible,
  });

  return (
    <div
      className={`terminal-module p-5 md:p-6 cursor-pointer transition-all duration-500 animate-fade-in-up ${isActive ? 'border-accent-500/40 shadow-[0_0_20px_rgba(200,168,64,0.08)]' : 'hover:-translate-y-1'}`}
      style={{ animationDelay: `${0.1 * (idx + 1)}s` }}
      onClick={onActivate}
    >
      {/* Header with live indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all duration-300 ${isActive ? 'bg-accent-500/10 border-accent-500/25' : 'bg-primary-500/8 border-primary-500/12'}`}>
          <i className={`${stat.icon} text-base transition-colors duration-300 ${isActive ? 'text-accent-400' : 'text-primary-400'}`}></i>
        </div>
        <LiveYoY label="" initial={stat.liveYoY.initial} range={stat.liveYoY.range} />
      </div>

      {/* Big Animated Number */}
      <div className={`mono-data text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-1.5 transition-all duration-300 ${isActive ? 'text-accent-400' : 'text-foreground-100'}`}>
        {visible ? formatted : '--'}
      </div>

      {/* Label + Detail */}
      <p className="text-xs md:text-[13px] text-foreground-400 font-medium mb-2">{stat.label}</p>
      <p className="text-[10px] md:text-[11px] text-foreground-600 font-light leading-relaxed mb-4">{stat.detail}</p>

      {/* Animated Sparkline with Hover Glow */}
      <AnimatedSparkline
        data={stat.trend}
        color={stat.color}
        active={isActive}
        onActivate={onActivate}
        idx={idx}
      />

      {/* Active state: data details reveal */}
      <div className={`mt-3 overflow-hidden transition-all duration-500 ${isActive ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="flex items-center justify-between text-[9px]">
          <div className="flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
            <span className="mono-data text-foreground-600">MAX: {Math.round(Math.max(...stat.trend) * (stat.countTarget > 50 ? 1 : stat.countTarget > 10 ? 10 : 1)).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-accent-400"></span>
            <span className="mono-data text-foreground-600">MIN: {Math.round(Math.min(...stat.trend) * (stat.countTarget > 50 ? 1 : stat.countTarget > 10 ? 10 : 1)).toLocaleString()}</span>
          </div>
          <span className="mono-data text-foreground-600">σ: {((Math.max(...stat.trend) - Math.min(...stat.trend)) / Math.max(...stat.trend) * 100).toFixed(1)}%</span>
        </div>
      </div>

      {/* Hover accent bar on left */}
      <div className={`absolute left-0 top-[10%] bottom-[10%] w-0.5 bg-accent-500/40 transition-transform duration-500 origin-top rounded-full ${isActive ? 'scale-y-100' : 'scale-y-0'}`}></div>
    </div>
  );
}